import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["super_admin", "admin", "manager", "sales", "marketing", "support", "editor", "finance", "client"];

export default function AdminUsers() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [] } = useQuery({ queryKey: ["admin-all-users"], queryFn: async () => (await api.get("/admin/users")).data });

  const setRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}`, { role }); toast.success("Role updated"); qc.invalidateQueries({ queryKey: ["admin-all-users"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (uid) => {
    if (!window.confirm("Delete user?")) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-all-users"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold">Users & Roles</h1>
      <p className="text-sm text-muted-foreground">{users.length} users · granular role-based access control</p>

      <div className="mt-6 overflow-hidden rounded-xl border border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase text-muted-foreground">
              <tr><th className="p-3">User</th><th className="p-3">Provider</th><th className="p-3">Role</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-border" data-testid={`user-row-${u.user_id}`}>
                  <td className="p-3"><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                  <td className="p-3"><Badge variant="outline">{u.auth_provider || "password"}</Badge></td>
                  <td className="p-3">
                    <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)} disabled={u.user_id === me?.user_id}>
                      <SelectTrigger className="w-40" data-testid={`user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    {u.user_id !== me?.user_id && <Button variant="ghost" size="icon" onClick={() => del(u.user_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
